<template>
  <div class="appointment-bubble">
    <BubbleHeader>{{message.message_description}}</BubbleHeader>
    <BubbleTextRow v-if="text"><BubbleTextMsg :text="text" /></BubbleTextRow>

    <MsgblContent :type="message.direction" v-if="message.showEntityLink">
      <MsgblLinkHeader icon="appointment"
                       :title="message.appointment_title"
                       @click="navigateTo" />
    </MsgblContent>

    <MsgblContent :type="message.direction" v-if="message.showEntityCard">
      <MsgblLinkHeader icon="appointment"
                       :title="message.appointment_title"
                       @click="navigateTo" />

      <MsgblTextRow class="when" v-if="times">
        <MsgblTextLabel v-if="isAppointmentRescheduledBubble" class="new-time-bold">{{$t('appointment.info.new_time')}}</MsgblTextLabel>
        <MsgblTextLabel v-else>{{$t('appointment.info.when')}}:</MsgblTextLabel>
        <MsgblTextValue :class="{'new-time-bold': isAppointmentRescheduledBubble}">
            <div v-if="times.length === 1"> {{times[0].start_time_h}}</div>
            <div v-else>
              <div v-for="(time, i) in times"
                  :key="i"
                  disabled="time.passed">
                {{time.start_time_h}}
              </div>
            </div>
        </MsgblTextValue>
      </MsgblTextRow>

      <MsgblTextRow class="when" v-if="isAppointmentRescheduledBubble && (messageable.previous_date || messageable.previous_date_proposed)">
        <MsgblTextLabel>{{$t('appointment.info.original_time')}}</MsgblTextLabel>
        <MsgblTextValue>
          <div> {{ originalTime }}</div>
        </MsgblTextValue>
      </MsgblTextRow>

      <MsgblTextRow class="where" v-if="showStaff && messageable.staff">
        <MsgblTextLabel>{{$t('booking.info.with')}}:</MsgblTextLabel>
        <MsgblTextValue>{{messageable.staff.display_name}}</MsgblTextValue>
      </MsgblTextRow>

      <MsgblTextRow class="where" v-if="messageable.where_h">
        <MsgblTextLabel>{{$t('appointment.info.where')}}:</MsgblTextLabel>
        <MsgblBooking :booking="messageable" />
      </MsgblTextRow>

      <MsgblTextRow class="amount-to-pay" v-if="messageable.payment_status && messageable.payment_status.amount_to_pay_h">
        <MsgblTextLabel data-qa="payment-status-state" :class="colorStatus">{{messageable.payment_status.state_summary.state_h}}:</MsgblTextLabel>
        <MsgblTextValue data-qa="payment-status-amount">{{price}}</MsgblTextValue>
      </MsgblTextRow>

      <MsgblActions v-if="actions" :actions="actions" @action="actionClicked" />
    </MsgblContent>
  </div>
</template>

<script>
import {
  BubbleHeader,
  BubbleTextRow,
  BubbleTextMsg,
  MsgblContent,
  MsgblTextRow,
  MsgblLinkHeader,
  MsgblActions,
  MsgblTextLabel,
  MsgblTextValue,
  MsgblBooking,
} from './helpers'

export default {
  name: 'AppointmentBubble',
  components: {
    BubbleHeader,
    BubbleTextRow,
    BubbleTextMsg,
    MsgblContent,
    MsgblTextRow,
    MsgblLinkHeader,
    MsgblActions,
    MsgblTextLabel,
    MsgblTextValue,
    MsgblBooking,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    showStaff: {
      type: Boolean,
      defualt: false
    }
  },
  computed: {
    messageable() {
      return this.message.messageable
    },
    text() {
      if(this.message.direction === 'business_to_client') {
        if(this.message.message_type === 'missed_note' || this.message.message_type === 'thank_you_note') {
          return this.messageable.follow_up_data
        }
      }

      if (this.message.text) return this.message.text

      if (this.message.firstOfMessageable && this.message.messageable.notes) return this.message.messageable.notes

      return null
    },
    state_summary() {
      return this.message.messageable.state_summary
    },
    times() {
      if (!this.state_summary) return

      switch (this.state_summary.state) {
        case 'requested':
        case 'reschedule':
          return this.messageable.optional_times
        default:  // 'scheduled', 'proposed_time', 'invited' that will have a start time, and other will have the calendar_item time
          return this.onlyStartTime
      }
    },
    onlyStartTime() {
      return [{
        start_time: this.messageable.calendar_start_time ,
        start_time_h: this.messageable.calendar_start_time_h,
        start_date_h: this.messageable.calendar_start_date_h,
        start_hour_h: this.messageable.calendar_start_hour_h
      }]
    },
    endState() {
      return ['scheduled','completed', 'done'].indexOf(this.state_summary.state) !== -1
    },
    firstFutureTime(){
      let selectedTime = null
      const times = this.times
      times && times.forEach(time => {
        if (!time.passed)
          selectedTime = time
          return false // break
      })

      return selectedTime
    },
    actions() {
      if (!this.state_summary) return
      let actions = this.state_summary.actions

      if (this.firstFutureTime === null) {
        actions = actions.filter(action => action.name !== 'accept')
      }

      if (this.endState) {
        if (this.messageable.no_show)
          actions.push({name: 'mark_show', name_h: this.$t('appointment.info.mark_show')})
        else
          actions.push({name: 'unmark_show', name_h: this.$t('appointment.info.unmark_show')})
      }

      actions.push({name: 'copy', name_h: this.$t('appointment.action.copy.title')})

      return actions
    },
    colorStatus() {
      const colorStatus = {
        paid:       'status-accent',
        created:    'status-alert',
        sent:       'status-alert',
        cancelled:  'status-warning',
        pending:    'ps-status-outstanding',
        overdue:    'ps-status-overdue',
        hidden:     'status-primary',
      }

      return colorStatus[this.messageable.payment_status.state_summary.state]
    },
    price(){
      if (['Invoice', 'Cart'].includes(this.messageable.payment_status.payable_type))
        return this.messageable.price_h;
      return this.messageable.payment_status.amount_to_pay_h;
    },
    isAppointmentRescheduledBubble(){
      return this.message.message_type === 'schedule_update' || this.message.message_type === "propose_time"
    },
    originalTime(){
      let previous_date = ""

      if (this.message.messageable.previous_date && this.message.message_type === 'schedule_update')
        previous_date = this.message.messageable.previous_date
      else if (this.message.messageable.previous_date_proposed && this.message.message_type === "propose_time")
        previous_date = this.message.messageable.previous_date_proposed

      return previous_date
    }
  },
  methods: {
    actionClicked(event) {
      const data = {appointment: this.messageable}
      this.$frontageApi.send('appointment-bubble-action', {action: event.action, data})
    },
    navigateTo() {
      this.$frontageApi.navigateTo('app.appointment.show', {uid: this.messageable.uid})
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/abstract/_variables';
@import '@/assets/scss/abstract/_i18n-mixins';

.new-time-bold {
  font-weight: 700;
  color: #0c1c28;
}

.appointment-bubble {

  .label {
    @include i-margin-mirror(0, 3px);
  }

  .when {
    display: flex;
  }

  .status-accent {
    color: $status_success;
  }

  .status-warning {
    color: $status_error;
  }

  .status-alert {
    color: $status_alert;
  }

  .status-disabled {
    color: $status_disabled;
  }

  .status-primary {
    color: $status_primary;
  }

  .ps-status-outstanding {
    color: $ps_outstanding
  }

  .ps-status-overdue {
    color: $ps_overdue
  }
}
</style>
